import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { cn } from "../../../lib/utils";
import type { ParsedEvent, Upload } from "../../../types/domain";
import { formatDateRange, formatDateTime, formatTime } from "../../../lib/baseline/format";
import { eventMatchesFilter, type TimelineFilter } from "../../../lib/events";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";

export interface TimelineRange {
  start: string;
  end: string;
  label: string;
}

interface TimelineLensProps {
  events: ParsedEvent[];
  upload?: Upload;
  rangeStart?: string;
  rangeEnd?: string;
  activeFilter: TimelineFilter;
  onRangeChange: (range: TimelineRange) => void;
  onClearRange: () => void;
  onFilterChange: (filter: TimelineFilter) => void;
}

type Lane = {
  label: string;
  color: string;
  id: LayerId;
  match: (event: ParsedEvent) => boolean;
};

type LayerId = "Watering" | "Programs" | "Zones" | "Flow" | "Two-wire" | "Config" | "Warnings" | "Critical";

type DragState =
  | { mode: "move"; durationMs: number }
  | { mode: "start"; fixedEndMs: number }
  | { mode: "end"; fixedStartMs: number };

type Bookmark = {
  id: string;
  label: string;
  range: TimelineRange;
  tone?: "critical" | "warning";
};

type IncidentBand = {
  label: string;
  tone: "normal" | "warning" | "critical" | "recovery";
  startMs: number;
  endMs: number;
};

const HOUR = 60 * 60 * 1000;
const BUCKET_COUNT = 56;

const lanes: Lane[] = [
  { id: "Watering", label: "Watering", color: "bg-sky-500", match: (event) => event.category === "WATERING" },
  {
    id: "Programs",
    label: "Programs",
    color: "bg-indigo-500",
    match: (event) => event.subjectCode === "PG" || event.category === "PROGRAM",
  },
  {
    id: "Zones",
    label: "Zones",
    color: "bg-emerald-600",
    match: (event) => event.subjectCode === "ZN" || event.category === "ZONE",
  },
  { id: "Flow", label: "Flow", color: "bg-cyan-700", match: (event) => event.category === "FLOW" },
  { id: "Two-wire", label: "Two-wire", color: "bg-amber-600", match: (event) => event.category === "TWO_WIRE" },
  { id: "Config", label: "Config", color: "bg-slate-600", match: (event) => event.category === "CONFIGURATION" },
];

const allLayerIds: LayerId[] = ["Watering", "Programs", "Zones", "Flow", "Two-wire", "Config", "Warnings", "Critical"];

const layerControls: Array<{ id: LayerId; color: string }> = [
  { id: "Watering", color: "bg-sky-500" },
  { id: "Programs", color: "bg-indigo-500" },
  { id: "Zones", color: "bg-emerald-600" },
  { id: "Flow", color: "bg-cyan-700" },
  { id: "Two-wire", color: "bg-amber-600" },
  { id: "Config", color: "bg-slate-600" },
  { id: "Warnings", color: "bg-amber-500" },
  { id: "Critical", color: "bg-red-600" },
];

const presetFilters: Array<{ filter: TimelineFilter; label: string; layers: LayerId[] }> = [
  { label: "Troubleshoot", filter: "PROBLEMS", layers: ["Two-wire", "Config", "Warnings", "Critical"] },
  { label: "Watering", filter: "WATERING", layers: ["Watering", "Programs", "Zones", "Flow"] },
  { label: "Electrical", filter: "TWO_WIRE", layers: ["Two-wire", "Warnings", "Critical", "Config"] },
  { label: "Zones", filter: "ZONES", layers: ["Zones", "Watering", "Warnings", "Critical"] },
  { label: "Programming", filter: "PROGRAMS", layers: ["Programs", "Config", "Watering"] },
  { label: "Client-safe", filter: "ALL", layers: allLayerIds },
];

export function TimelineLens({
  events,
  upload,
  rangeStart,
  rangeEnd,
  activeFilter,
  onRangeChange,
  onClearRange,
  onFilterChange,
}: TimelineLensProps) {
  const model = useMemo(() => buildTimelineModel(events, rangeStart, rangeEnd), [events, rangeEnd, rangeStart]);
  const [visibleLayers, setVisibleLayers] = useState<LayerId[]>(allLayerIds);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragBoundsRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    if (!dragState || !model) return;
    const currentDrag: DragState = dragState;
    const currentModel = model;

    function msFromDragClientX(clientX: number) {
      const bounds = dragBoundsRef.current;
      if (!bounds) return currentModel.startMs;
      const ratio = clamp((clientX - bounds.left) / bounds.width, 0, 1);
      return currentModel.startMs + ratio * (currentModel.endMs - currentModel.startMs);
    }

    function emitDragRange(startMs: number, endMs: number) {
      onRangeChange({
        start: timestampFromMs(startMs),
        end: timestampFromMs(endMs),
        label: "Custom window",
      });
    }

    function onPointerMove(event: PointerEvent) {
      const pointerMs = msFromDragClientX(event.clientX);
      if (currentDrag.mode === "move") {
        const safeDuration = clamp(currentDrag.durationMs, 60 * 1000, currentModel.endMs - currentModel.startMs);
        const nextStart = clamp(pointerMs - safeDuration / 2, currentModel.startMs, currentModel.endMs - safeDuration);
        emitDragRange(nextStart, nextStart + safeDuration);
      }
      if (currentDrag.mode === "start") {
        emitDragRange(clamp(pointerMs, currentModel.startMs, currentDrag.fixedEndMs - 60 * 1000), currentDrag.fixedEndMs);
      }
      if (currentDrag.mode === "end") {
        emitDragRange(currentDrag.fixedStartMs, clamp(pointerMs, currentDrag.fixedStartMs + 60 * 1000, currentModel.endMs));
      }
    }

    function onPointerUp() {
      setDragState(null);
      dragBoundsRef.current = null;
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [dragState, model, onRangeChange]);

  if (!model) return null;

  const timelineModel = model;
  const activeRange = timelineModel.activeRange;
  const visibleLayerSet = new Set(visibleLayers);

  function emitRange(startMs: number, endMs: number, label = "Custom window") {
    onRangeChange({
      start: timestampFromMs(startMs),
      end: timestampFromMs(endMs),
      label,
    });
  }

  function msFromClientX(clientX: number) {
    const bounds = dragBoundsRef.current;
    if (!bounds) return timelineModel.startMs;
    const ratio = clamp((clientX - bounds.left) / bounds.width, 0, 1);
    return timelineModel.startMs + ratio * (timelineModel.endMs - timelineModel.startMs);
  }

  function beginDrag(event: ReactPointerEvent<HTMLElement>, nextDragState: DragState) {
    if (event.button !== 0) return;
    event.preventDefault();
    const track = event.currentTarget.closest("[data-timeline-track]") as HTMLElement | null;
    dragBoundsRef.current = (track ?? event.currentTarget).getBoundingClientRect();
    setDragState(nextDragState);
  }

  function beginMoveDrag(event: ReactPointerEvent<HTMLElement>) {
    beginDrag(event, { mode: "move", durationMs: activeRange.endMs - activeRange.startMs });
    const centerMs = msFromClientX(event.clientX);
    moveRange(centerMs, activeRange.endMs - activeRange.startMs);
  }

  function moveRange(centerMs: number, durationMs: number) {
    const safeDuration = clamp(durationMs, 60 * 1000, timelineModel.endMs - timelineModel.startMs);
    const nextStart = clamp(centerMs - safeDuration / 2, timelineModel.startMs, timelineModel.endMs - safeDuration);
    emitRange(nextStart, nextStart + safeDuration);
  }

  function resizeStart(pointerMs: number, fixedEndMs: number) {
    emitRange(clamp(pointerMs, timelineModel.startMs, fixedEndMs - 60 * 1000), fixedEndMs);
  }

  function resizeEnd(pointerMs: number, fixedStartMs: number) {
    emitRange(fixedStartMs, clamp(pointerMs, fixedStartMs + 60 * 1000, timelineModel.endMs));
  }

  function toggleLayer(layerId: LayerId) {
    setVisibleLayers((current) => {
      if (current.includes(layerId) && current.length > 1) return current.filter((item) => item !== layerId);
      if (current.includes(layerId)) return current;
      return [...current, layerId];
    });
  }

  function selectPreset(preset: (typeof presetFilters)[number]) {
    setVisibleLayers(preset.layers);
    onFilterChange(preset.filter);
    if (preset.filter === "ALL") {
      onClearRange();
      return;
    }
    const focused = events.filter((event) => eventMatchesFilter(event, preset.filter));
    if (!focused.length) return;
    const issue = focused.find((event) => event.severity !== "INFO");
    if (issue) {
      onRangeChange(eventContextRange(issue, timelineModel.startMs, timelineModel.endMs));
      return;
    }
    const busiest = busiestWindow(focused, timelineModel.startMs, timelineModel.endMs);
    emitRange(busiest.startMs, busiest.endMs, preset.label);
  }

  function selectLayerRange(layer: LayerId) {
    const layerEvents =
      layer === "Warnings"
        ? timelineModel.warningEvents
        : layer === "Critical"
          ? timelineModel.criticalEvents
          : (timelineModel.eventsByLane.get(layer) ?? []);
    if (!layerEvents.length) return;
    const issue = layerEvents.find((event) => event.severity !== "INFO");
    if (issue) {
      onRangeChange(eventContextRange(issue, timelineModel.startMs, timelineModel.endMs));
      return;
    }
    const busiest = busiestWindow(layerEvents, timelineModel.startMs, timelineModel.endMs);
    emitRange(busiest.startMs, busiest.endMs, layer);
  }

  function zoomTo(durationMs: number, label: string) {
    const safeDuration = clamp(durationMs, 60 * 1000, timelineModel.endMs - timelineModel.startMs);
    const centerMs = activeRange.startMs + (activeRange.endMs - activeRange.startMs) / 2;
    const nextStart = clamp(centerMs - safeDuration / 2, timelineModel.startMs, timelineModel.endMs - safeDuration);
    emitRange(nextStart, nextStart + safeDuration, label);
  }

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-panel">
      <header className="grid gap-4 border-b border-slate-200 p-5 xl:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <p className="text-xs font-black uppercase text-water-600">Timeline Lens</p>
          <h2 className="mt-1 text-2xl font-black leading-tight text-navy-950">
            Start with the controller&apos;s whole history, then zoom into meaning.
          </h2>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
            Time is the first navigation object. The lanes show activity density, incident state,
            warnings, critical events, and the selected investigation window before the user chooses
            a problem, zone, program, or device.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge className="bg-slate-50 text-slate-700 ring-slate-200">
              {formatDateRange(model.firstEvent.timestamp, model.lastEvent.timestamp)}
            </Badge>
            <Badge className="bg-slate-50 text-slate-700 ring-slate-200">
              {events.length.toLocaleString()} events
            </Badge>
            <Badge className="bg-amber-50 text-amber-800 ring-amber-200">
              {model.warningCount.toLocaleString()} warnings
            </Badge>
            <Badge className="bg-red-50 text-red-800 ring-red-200">
              {model.criticalCount.toLocaleString()} critical
            </Badge>
            {upload?.controllerName ? (
              <Badge className="bg-slate-50 text-slate-700 ring-slate-200">{upload.controllerName}</Badge>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-start gap-2 xl:justify-end">
          <div className="flex rounded-md border border-slate-200 bg-slate-100 p-1">
            {[
              { label: "Fit", action: onClearRange, active: !rangeStart && !rangeEnd },
              { label: "Day", action: () => zoomTo(24 * HOUR, "Day view"), active: activeRange.endMs - activeRange.startMs > 3 * HOUR },
              { label: "Hour", action: () => zoomTo(HOUR, "Hour view"), active: activeRange.endMs - activeRange.startMs <= 3 * HOUR && activeRange.endMs - activeRange.startMs > 10 * 60 * 1000 },
              { label: "Minute", action: () => zoomTo(15 * 60 * 1000, "Minute view"), active: activeRange.endMs - activeRange.startMs <= 10 * 60 * 1000 },
            ].map((item) => (
              <button
                key={item.label}
                className={cn(
                  "min-h-8 rounded px-3 text-xs font-black text-slate-700",
                  item.active ? "bg-white text-navy-950 shadow-sm" : "",
                )}
                type="button"
                onClick={item.action}
              >
                {item.label}
              </button>
            ))}
          </div>
          <Button size="sm" type="button" variant="secondary" onClick={onClearRange}>
            Fit all
          </Button>
        </div>
      </header>

      <div className="grid gap-4 border-b border-slate-200 bg-gradient-to-r from-sky-50 via-white to-orange-50 p-5 xl:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <p className="text-xs font-black uppercase text-water-600">Controller story</p>
          <p className="mt-1 max-w-5xl text-sm leading-6 text-slate-700">
            <strong className="text-navy-950">{model.storyLead}</strong> {model.storyDetail}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 xl:justify-end">
          <Badge className="bg-slate-50 text-slate-700 ring-slate-200">
            Cause proximity: {model.proximityLabel}
          </Badge>
          <Badge className="bg-amber-50 text-amber-800 ring-amber-200">
            Incident band: {model.incidentDurationLabel}
          </Badge>
          <Badge className="bg-red-50 text-red-800 ring-red-200">{model.reviewLabel}</Badge>
        </div>
      </div>

      <div className="grid gap-3 border-b border-slate-200 bg-white px-5 py-4 lg:grid-cols-[7rem_minmax(0,1fr)]">
        <p className="pt-2 text-xs font-black uppercase text-slate-500">View presets</p>
        <div className="flex flex-wrap gap-2">
          {presetFilters.map((preset) => (
            <button
              key={preset.label}
              className={cn(
                "min-h-8 rounded-full border px-3 text-xs font-black",
                preset.filter === activeFilter
                  ? "border-navy-950 bg-navy-950 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              )}
              type="button"
              onClick={() => selectPreset(preset)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <p className="pt-2 text-xs font-black uppercase text-slate-500">Visible layers</p>
        <div className="flex flex-wrap gap-2">
          {layerControls.map((layer) => {
            const isVisible = visibleLayerSet.has(layer.id);
            return (
            <button
              key={layer.id}
              className={cn(
                "inline-flex min-h-8 items-center gap-2 rounded-full border px-3 text-xs font-black",
                isVisible ? "border-slate-300 bg-white text-slate-800" : "border-slate-200 bg-slate-100 text-slate-400",
              )}
              type="button"
              onClick={() => toggleLayer(layer.id)}
              onDoubleClick={() => selectLayerRange(layer.id)}
            >
              <span className={cn("h-2 w-2 rounded-full", isVisible ? layer.color : "bg-slate-300")} />
              {layer.id}
            </button>
          );
          })}
        </div>
      </div>

      <div className="grid gap-4 border-b border-slate-200 bg-slate-50 p-5 lg:grid-cols-[minmax(12rem,0.42fr)_minmax(0,1fr)_minmax(12rem,0.42fr)]">
        <RangeAnchor label="Beginning" event={model.firstEvent} />
        <div className="self-center text-center text-xs font-black text-slate-500">
          Full export range visible before the event list becomes the detail layer.
        </div>
        <RangeAnchor label="End" event={model.lastEvent} />
      </div>

      <div className="p-5">
        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-2">
              <div className="text-xs font-black uppercase text-slate-500">Export range</div>
              <div className="grid grid-cols-6 border-b border-slate-300 pb-2">
                {model.ticks.map((tick) => (
                  <span key={tick} className="text-xs font-black text-slate-500">
                    {tick}
                  </span>
                ))}
              </div>

              <LaneLabel>Incident state</LaneLabel>
              <div
                className="relative h-9 cursor-grab overflow-hidden rounded-md border border-slate-200 bg-white active:cursor-grabbing"
                data-timeline-track
                onPointerDown={beginMoveDrag}
              >
                {model.incidentBands.map((band) => (
                  <button
                    key={`${band.label}-${band.startMs}`}
                    className={cn(
                      "absolute top-2 h-5 rounded-full px-2 text-[10px] font-black text-white",
                      band.tone === "normal" ? "bg-slate-500" : "",
                      band.tone === "warning" ? "bg-amber-600" : "",
                      band.tone === "critical" ? "bg-red-600" : "",
                      band.tone === "recovery" ? "bg-teal-700" : "",
                    )}
                    style={rangeStyle(band.startMs, band.endMs, model.startMs, model.endMs)}
                    type="button"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() =>
                      onRangeChange({
                        start: timestampFromMs(band.startMs),
                        end: timestampFromMs(band.endMs),
                        label: band.label,
                      })
                    }
                  >
                    {band.label}
                  </button>
                ))}
                <SelectionOverlay
                  activeRange={activeRange}
                  endMs={model.endMs}
                  startMs={model.startMs}
                  onEndResize={(event) => beginDrag(event, { mode: "end", fixedStartMs: activeRange.startMs })}
                  onStartResize={(event) => beginDrag(event, { mode: "start", fixedEndMs: activeRange.endMs })}
                />
              </div>

              {lanes.filter((lane) => visibleLayerSet.has(lane.id)).map((lane) => (
                <LaneBars
                  key={lane.label}
                  activeRange={activeRange}
                  endMs={model.endMs}
                  lane={lane}
                  laneEvents={model.eventsByLane.get(lane.label) ?? []}
                  startMs={model.startMs}
                  onEndResize={(event) => beginDrag(event, { mode: "end", fixedStartMs: activeRange.startMs })}
                  onLaneFocus={() => selectLayerRange(lane.id)}
                  onMoveRange={beginMoveDrag}
                  onStartResize={(event) => beginDrag(event, { mode: "start", fixedEndMs: activeRange.endMs })}
                />
              ))}

              {visibleLayerSet.has("Warnings") ? (
                <>
                  <LaneLabel onClick={() => selectLayerRange("Warnings")}>Warnings</LaneLabel>
                  <MarkerLane
                    activeRange={activeRange}
                    endMs={model.endMs}
                    events={model.warningEvents}
                    markerClassName="bg-amber-500"
                    startMs={model.startMs}
                    onEndResize={(event) => beginDrag(event, { mode: "end", fixedStartMs: activeRange.startMs })}
                    onEventSelect={(event) => onRangeChange(eventContextRange(event, model.startMs, model.endMs))}
                    onMoveRange={beginMoveDrag}
                    onStartResize={(event) => beginDrag(event, { mode: "start", fixedEndMs: activeRange.endMs })}
                  />
                </>
              ) : null}

              {visibleLayerSet.has("Critical") ? (
                <>
                  <LaneLabel onClick={() => selectLayerRange("Critical")}>Critical</LaneLabel>
                  <MarkerLane
                    activeRange={activeRange}
                    endMs={model.endMs}
                    events={model.criticalEvents}
                    markerClassName="bg-red-600"
                    startMs={model.startMs}
                    onEndResize={(event) => beginDrag(event, { mode: "end", fixedStartMs: activeRange.startMs })}
                    onEventSelect={(event) => onRangeChange(eventContextRange(event, model.startMs, model.endMs))}
                    onMoveRange={beginMoveDrag}
                    onStartResize={(event) => beginDrag(event, { mode: "start", fixedEndMs: activeRange.endMs })}
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-4">
          {model.callouts.map((callout) => (
            <button
              key={callout.title}
              className={cn(
                "rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm",
                callout.tone === "critical" ? "border-l-4 border-l-red-500" : "",
                callout.tone === "warning" ? "border-l-4 border-l-amber-500" : "",
                callout.tone === "config" ? "border-l-4 border-l-slate-500" : "",
                callout.tone === "info" ? "border-l-4 border-l-water-400" : "",
              )}
              type="button"
              onClick={() => onRangeChange(callout.range)}
            >
              <span className="block text-sm font-black leading-tight text-navy-950">{callout.title}</span>
              <span className="mt-1 block text-xs leading-5 text-slate-600">{callout.detail}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 lg:grid-cols-[7rem_minmax(0,1fr)]">
          <p className="pt-2 text-xs font-black uppercase text-slate-500">Jump points</p>
          <div className="flex flex-wrap gap-2">
            {model.bookmarks.map((bookmark) => (
              <button
                key={bookmark.id}
                className={cn(
                  "min-h-8 rounded-full border px-3 text-xs font-black",
                  bookmark.tone === "critical"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : bookmark.tone === "warning"
                      ? "border-amber-200 bg-amber-50 text-amber-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                )}
                type="button"
                onClick={() => onRangeChange(bookmark.range)}
              >
                {bookmark.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[4.5rem_minmax(0,1fr)_4.5rem] items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <span className="text-xs font-black text-slate-500">{formatShortRangeEdge(model.firstEvent.timestamp)}</span>
          <div className="relative h-3 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-gradient-to-r from-sky-300 via-teal-500 to-red-500" />
            <div
              className="absolute top-0 h-full rounded-full bg-navy-950"
              style={rangeStyle(activeRange.startMs, activeRange.endMs, model.startMs, model.endMs)}
            />
          </div>
          <span className="text-right text-xs font-black text-slate-500">
            {formatShortRangeEdge(model.lastEvent.timestamp)}
          </span>
        </div>
      </div>

      <div className="grid gap-4 border-t border-slate-200 bg-slate-50 p-5 xl:grid-cols-[minmax(0,0.96fr)_minmax(22rem,1.04fr)]">
        <section>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-black text-navy-950">Selected window: {model.activeRange.label}</h3>
              <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">
                The event list below is filtered by this investigation window. Choose a bookmark or incident
                band to move through time.
              </p>
            </div>
            <Badge className="bg-slate-50 text-slate-700 ring-slate-200">Semantic zoom: Hour</Badge>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Events" value={model.selectedEvents.length.toLocaleString()} />
            <Metric label="Critical" value={model.selectedCriticalCount.toLocaleString()} />
            <Metric label="Warnings" value={model.selectedWarningCount.toLocaleString()} />
            <Metric label="Zones" value={model.selectedZonesCount.toLocaleString()} />
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
              <h3 className="text-base font-black text-navy-950">Before / after the anchor event</h3>
              <Badge className="bg-red-50 text-red-700 ring-red-200">Anchor: {formatTime(model.anchorEvent.timestamp)}</Badge>
            </div>
            <div className="grid md:grid-cols-2">
              <CompareColumn title="60 minutes before" summary={model.beforeSummary} items={model.beforeItems} />
              <CompareColumn title="60 minutes after" summary={model.afterSummary} items={model.afterItems} className="border-t border-slate-200 md:border-l md:border-t-0" />
            </div>
          </div>

          <div className="mt-4 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-3">
            {model.proximityItems.map((item) => (
              <div key={item.label}>
                <p className="text-xs font-black uppercase text-slate-500">{item.label}</p>
                <p className="mt-1 text-sm font-black leading-5 text-navy-950">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-3">
            {model.sequenceCards.map((card) => (
              <button
                key={`${card.title}-${card.time}`}
                className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm sm:grid-cols-[5rem_minmax(0,1fr)_auto]"
                type="button"
                onClick={() => onRangeChange(card.range)}
              >
                <span className="text-sm font-black text-slate-500">{card.time}</span>
                <span>
                  <span className="block text-base font-black leading-tight text-navy-950">{card.title}</span>
                  <span className="mt-1 block text-sm leading-6 text-slate-600">{card.detail}</span>
                </span>
                <span className="self-start rounded-full bg-sky-50 px-3 py-1 text-xs font-black text-sky-700">
                  {card.count.toLocaleString()} events
                </span>
              </button>
            ))}
          </div>
        </section>

        <aside className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
            <div>
              <h3 className="text-lg font-black text-navy-950">Events in this window</h3>
              <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">
                Representative events from the active time range.
              </p>
            </div>
            <Badge className="bg-slate-50 text-slate-700 ring-slate-200">
              {model.selectedEvents.length.toLocaleString()} total
            </Badge>
          </div>
          <div className="divide-y divide-slate-100">
            {model.representativeEvents.map((event) => (
              <button
                key={event.eventId}
                className="grid w-full gap-3 p-4 text-left hover:bg-slate-50 sm:grid-cols-[4.5rem_minmax(0,1fr)_auto]"
                type="button"
                onClick={() =>
                  onRangeChange({
                    start: timestampFromMs(localTimestampMs(event.timestamp) - 15 * 60 * 1000),
                    end: timestampFromMs(localTimestampMs(event.timestamp) + 15 * 60 * 1000),
                    label: `${formatTime(event.timestamp)} event context`,
                  })
                }
              >
                <span className="text-sm font-black text-slate-500">{formatTime(event.timestamp)}</span>
                <span>
                  <span className="block text-base font-black leading-tight text-navy-950">{event.title}</span>
                  <span className="mt-1 block text-sm leading-6 text-slate-600">{event.summary}</span>
                </span>
                <Badge className={event.severity === "CRITICAL" ? "bg-red-50 text-red-800 ring-red-200" : event.severity === "WARNING" ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-slate-50 text-slate-700 ring-slate-200"}>
                  {event.severity === "INFO" ? event.category.replace("_", " ") : event.severity}
                </Badge>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

export function getDefaultTimelineRange(events: ParsedEvent[]): TimelineRange | null {
  const sorted = sortOldest(events);
  if (!sorted.length) return null;

  const startMs = localTimestampMs(sorted[0].timestamp);
  const endMs = localTimestampMs(sorted[sorted.length - 1].timestamp);
  const critical = sorted.find((event) => event.severity === "CRITICAL");
  const warning = sorted.find((event) => event.severity === "WARNING" || event.severity === "CRITICAL");

  if (critical || warning) {
    const anchor = critical ?? warning!;
    const anchorMs = localTimestampMs(anchor.timestamp);
    const rangeStart = Math.max(startMs, anchorMs - HOUR);
    const rangeEnd = Math.min(endMs, Math.max(anchorMs + 2 * HOUR, rangeStart + HOUR));
    return {
      start: timestampFromMs(rangeStart),
      end: timestampFromMs(rangeEnd),
      label: `${formatTime(timestampFromMs(rangeStart))} - ${formatTime(timestampFromMs(rangeEnd))}`,
    };
  }

  const busiest = busiestWindow(sorted, startMs, endMs);
  return {
    start: timestampFromMs(busiest.startMs),
    end: timestampFromMs(busiest.endMs),
    label: `${formatTime(timestampFromMs(busiest.startMs))} - ${formatTime(timestampFromMs(busiest.endMs))}`,
  };
}

function buildTimelineModel(events: ParsedEvent[], rangeStart?: string, rangeEnd?: string) {
  const sorted = sortOldest(events);
  if (!sorted.length) return null;

  const firstEvent = sorted[0];
  const lastEvent = sorted[sorted.length - 1];
  const startMs = localTimestampMs(firstEvent.timestamp);
  const endMs = Math.max(localTimestampMs(lastEvent.timestamp), startMs + 60 * 1000);
  const warningEvents = sorted.filter((event) => event.severity === "WARNING");
  const criticalEvents = sorted.filter((event) => event.severity === "CRITICAL");
  const issueEvents = sorted.filter((event) => event.severity !== "INFO");
  const defaultRange = getDefaultTimelineRange(sorted)!;
  const activeRange = normalizeRange(rangeStart, rangeEnd, defaultRange, startMs, endMs);
  const selectedEvents = sorted.filter((event) => {
    const value = localTimestampMs(event.timestamp);
    return value >= activeRange.startMs && value <= activeRange.endMs;
  });
  const anchorEvent = criticalEvents.find((event) => inRange(event, activeRange)) ?? issueEvents.find((event) => inRange(event, activeRange)) ?? selectedEvents[0] ?? firstEvent;
  const anchorMs = localTimestampMs(anchorEvent.timestamp);
  const beforeEvents = sorted.filter((event) => {
    const value = localTimestampMs(event.timestamp);
    return value >= anchorMs - HOUR && value < anchorMs;
  });
  const afterEvents = sorted.filter((event) => {
    const value = localTimestampMs(event.timestamp);
    return value > anchorMs && value <= anchorMs + HOUR;
  });
  const incidentBands = buildIncidentBands(sorted, startMs, endMs);
  const bookmarks = buildBookmarks(sorted, startMs, endMs);
  const callouts = buildCallouts(sorted, bookmarks, defaultRange);
  const eventsByLane = new Map(lanes.map((lane) => [lane.label, sorted.filter(lane.match)]));
  const sequenceCards = buildSequenceCards(selectedEvents, activeRange);
  const representativeEvents = representative(selectedEvents);
  const warningCount = warningEvents.length;
  const criticalCount = criticalEvents.length;
  const selectedWarningCount = selectedEvents.filter((event) => event.severity === "WARNING").length;
  const selectedCriticalCount = selectedEvents.filter((event) => event.severity === "CRITICAL").length;
  const selectedZonesCount = new Set(selectedEvents.map((event) => event.zoneNumber).filter(Boolean)).size;
  const firstCritical = criticalEvents[0];
  const firstWarning = issueEvents[0];
  const firstConfigBeforeIssue = firstCritical
    ? sorted.find((event) => event.category === "CONFIGURATION" && localTimestampMs(event.timestamp) >= localTimestampMs(firstCritical.timestamp) - HOUR && localTimestampMs(event.timestamp) <= localTimestampMs(firstCritical.timestamp))
    : undefined;
  const proximityMinutes =
    firstCritical && firstConfigBeforeIssue
      ? Math.max(1, Math.round((localTimestampMs(firstCritical.timestamp) - localTimestampMs(firstConfigBeforeIssue.timestamp)) / 60000))
      : undefined;
  const incidentDurationMs =
    criticalEvents.length > 1
      ? localTimestampMs(criticalEvents[criticalEvents.length - 1].timestamp) - localTimestampMs(criticalEvents[0].timestamp)
      : selectedEvents.length
        ? activeRange.endMs - activeRange.startMs
        : 0;

  return {
    activeRange,
    afterItems: compareItems(afterEvents),
    afterSummary: summarizeCompare(afterEvents),
    anchorEvent,
    beforeItems: compareItems(beforeEvents),
    beforeSummary: summarizeCompare(beforeEvents),
    bookmarks,
    callouts,
    criticalCount,
    criticalEvents,
    endMs,
    eventsByLane,
    firstEvent,
    incidentBands,
    incidentDurationLabel: formatDuration(incidentDurationMs),
    lastEvent,
    proximityItems: buildProximityItems(firstCritical, firstConfigBeforeIssue, selectedEvents),
    proximityLabel: proximityMinutes ? `${proximityMinutes} min` : "none found",
    representativeEvents,
    reviewLabel: criticalCount ? "Needs review" : warningCount ? "Review warnings" : "No issue cluster",
    selectedCriticalCount,
    selectedEvents,
    selectedWarningCount,
    selectedZonesCount,
    sequenceCards,
    startMs,
    storyDetail: buildStoryDetail(firstWarning, firstCritical, warningCount, criticalCount),
    storyLead: buildStoryLead(warningCount, criticalCount),
    ticks: buildTicks(startMs, endMs),
    warningCount,
    warningEvents,
  };
}

function RangeAnchor({ event, label }: { event: ParsedEvent; label: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-black uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-black text-navy-950">{formatDateTime(event.timestamp)}</p>
      <p className="mt-1 text-sm leading-5 text-slate-600">{event.title}</p>
    </div>
  );
}

function LaneLabel({ children, onClick }: { children: string; onClick?: () => void }) {
  if (onClick) {
    return (
      <button
        className="flex h-8 items-center justify-end rounded px-2 text-right text-xs font-black text-slate-700 hover:bg-slate-100"
        type="button"
        onClick={onClick}
      >
        {children}
      </button>
    );
  }

  return (
    <div className="flex h-8 items-center justify-end pr-2 text-right text-xs font-black text-slate-700">
      {children}
    </div>
  );
}

function LaneBars({
  activeRange,
  endMs,
  lane,
  laneEvents,
  onEndResize,
  onLaneFocus,
  onMoveRange,
  onStartResize,
  startMs,
}: {
  activeRange: ActiveRange;
  endMs: number;
  lane: Lane;
  laneEvents: ParsedEvent[];
  onEndResize: (event: ReactPointerEvent<HTMLElement>) => void;
  onLaneFocus: () => void;
  onMoveRange: (event: ReactPointerEvent<HTMLElement>) => void;
  onStartResize: (event: ReactPointerEvent<HTMLElement>) => void;
  startMs: number;
}) {
  const bars = bucketEvents(laneEvents, startMs, endMs);
  return (
    <>
      <LaneLabel onClick={onLaneFocus}>{lane.label}</LaneLabel>
      <div
        className="relative h-8 cursor-grab overflow-hidden rounded-md bg-slate-100 active:cursor-grabbing"
        data-timeline-track
        onPointerDown={onMoveRange}
      >
        {bars.map((bar) => (
          <span
            key={`${lane.label}-${bar.index}`}
            className={cn("absolute bottom-1 rounded-sm", lane.color)}
            style={{
              height: `${bar.height}px`,
              left: `${bar.left}%`,
              width: `${bar.width}%`,
            }}
          />
        ))}
        <SelectionOverlay
          activeRange={activeRange}
          endMs={endMs}
          startMs={startMs}
          onEndResize={onEndResize}
          onStartResize={onStartResize}
        />
      </div>
    </>
  );
}

function MarkerLane({
  activeRange,
  endMs,
  events,
  markerClassName,
  onEndResize,
  onEventSelect,
  onMoveRange,
  onStartResize,
  startMs,
}: {
  activeRange: ActiveRange;
  endMs: number;
  events: ParsedEvent[];
  markerClassName: string;
  onEndResize: (event: ReactPointerEvent<HTMLElement>) => void;
  onEventSelect: (event: ParsedEvent) => void;
  onMoveRange: (event: ReactPointerEvent<HTMLElement>) => void;
  onStartResize: (event: ReactPointerEvent<HTMLElement>) => void;
  startMs: number;
}) {
  return (
    <div
      className="relative h-8 cursor-grab overflow-hidden rounded-md bg-slate-100 active:cursor-grabbing"
      data-timeline-track
      onPointerDown={onMoveRange}
    >
      {events.slice(0, 80).map((event) => (
        <button
          key={event.eventId}
          className={cn("absolute top-2 h-3 w-3 rounded-full border-2 border-white shadow-sm", markerClassName)}
          style={{ left: `calc(${percent(localTimestampMs(event.timestamp), startMs, endMs)}% - 6px)` }}
          type="button"
          aria-label={`${event.severity.toLowerCase()} at ${formatTime(event.timestamp)}`}
          onPointerDown={(pointerEvent) => pointerEvent.stopPropagation()}
          onClick={() => onEventSelect(event)}
        />
      ))}
      <SelectionOverlay
        activeRange={activeRange}
        endMs={endMs}
        startMs={startMs}
        onEndResize={onEndResize}
        onStartResize={onStartResize}
      />
    </div>
  );
}

function SelectionOverlay({
  activeRange,
  endMs,
  onEndResize,
  onStartResize,
  startMs,
}: {
  activeRange: ActiveRange;
  endMs: number;
  onEndResize: (event: ReactPointerEvent<HTMLElement>) => void;
  onStartResize: (event: ReactPointerEvent<HTMLElement>) => void;
  startMs: number;
}) {
  return (
    <span
      className="pointer-events-none absolute inset-y-0 border-x-2 border-navy-950 bg-sky-200/20"
      style={rangeStyle(activeRange.startMs, activeRange.endMs, startMs, endMs)}
    >
      <button
        aria-label="Drag start of selected time window"
        className="pointer-events-auto absolute -left-1 top-1/2 h-6 w-2 -translate-y-1/2 rounded-full bg-navy-950 shadow-sm"
        type="button"
        onPointerDown={(event) => {
          event.stopPropagation();
          onStartResize(event);
        }}
      />
      <button
        aria-label="Drag end of selected time window"
        className="pointer-events-auto absolute -right-1 top-1/2 h-6 w-2 -translate-y-1/2 rounded-full bg-navy-950 shadow-sm"
        type="button"
        onPointerDown={(event) => {
          event.stopPropagation();
          onEndResize(event);
        }}
      />
    </span>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-black uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-black text-navy-950">{value}</p>
    </div>
  );
}

function CompareColumn({
  className,
  items,
  summary,
  title,
}: {
  className?: string;
  items: string[];
  summary: string;
  title: string;
}) {
  return (
    <div className={cn("p-4", className)}>
      <p className="text-xs font-black uppercase text-slate-500">{title}</p>
      <p className="mt-1 text-sm font-black leading-5 text-navy-950">{summary}</p>
      <ul className="mt-3 space-y-1 text-sm leading-5 text-slate-600">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

type ActiveRange = TimelineRange & {
  startMs: number;
  endMs: number;
};

function sortOldest(events: ParsedEvent[]) {
  return [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

function localTimestampMs(timestamp: string) {
  const [date, time = "00:00:00"] = timestamp.split("T");
  const [year, month, day] = date.split("-").map(Number);
  const [hour = 0, minute = 0, second = 0] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hour, minute, second).getTime();
}

function timestampFromMs(value: number) {
  const date = new Date(value);
  const pad = (input: number) => String(input).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function normalizeRange(
  rangeStart: string | undefined,
  rangeEnd: string | undefined,
  fallback: TimelineRange,
  startMs: number,
  endMs: number,
): ActiveRange {
  const rawStart = rangeStart ? localTimestampMs(rangeStart) : localTimestampMs(fallback.start);
  const rawEnd = rangeEnd ? localTimestampMs(rangeEnd) : localTimestampMs(fallback.end);
  const safeStart = clamp(Math.min(rawStart, rawEnd), startMs, endMs);
  const safeEnd = clamp(Math.max(rawStart, rawEnd), safeStart + 60 * 1000, endMs);
  return {
    start: timestampFromMs(safeStart),
    end: timestampFromMs(safeEnd),
    label: `${formatDateTime(timestampFromMs(safeStart))} - ${formatTime(timestampFromMs(safeEnd))}`,
    startMs: safeStart,
    endMs: safeEnd,
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function percent(value: number, startMs: number, endMs: number) {
  if (endMs <= startMs) return 0;
  return clamp(((value - startMs) / (endMs - startMs)) * 100, 0, 100);
}

function rangeStyle(rangeStartMs: number, rangeEndMs: number, startMs: number, endMs: number) {
  const left = percent(rangeStartMs, startMs, endMs);
  const right = percent(rangeEndMs, startMs, endMs);
  return {
    left: `${left}%`,
    width: `${Math.max(0.7, right - left)}%`,
  };
}

function bucketEvents(events: ParsedEvent[], startMs: number, endMs: number) {
  const buckets = Array.from({ length: BUCKET_COUNT }, (_, index) => ({ count: 0, index }));
  for (const event of events) {
    const value = localTimestampMs(event.timestamp);
    const index = clamp(Math.floor((percent(value, startMs, endMs) / 100) * BUCKET_COUNT), 0, BUCKET_COUNT - 1);
    buckets[index].count += 1;
  }
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return buckets
    .filter((bucket) => bucket.count > 0)
    .map((bucket) => ({
      height: 6 + Math.round((bucket.count / max) * 19),
      index: bucket.index,
      left: (bucket.index / BUCKET_COUNT) * 100,
      width: 82 / BUCKET_COUNT,
    }));
}

function buildTicks(startMs: number, endMs: number) {
  const formatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
  return Array.from({ length: 6 }, (_, index) => {
    const value = startMs + ((endMs - startMs) / 5) * index;
    return formatter.format(new Date(value));
  });
}

function buildIncidentBands(events: ParsedEvent[], startMs: number, endMs: number): IncidentBand[] {
  const firstIssue = events.find((event) => event.severity !== "INFO");
  const firstCritical = events.find((event) => event.severity === "CRITICAL");
  const criticalEvents = events.filter((event) => event.severity === "CRITICAL");
  if (!firstIssue) {
    return [{ label: "Normal", tone: "normal", startMs, endMs }];
  }

  const firstIssueMs = localTimestampMs(firstIssue.timestamp);
  const firstCriticalMs = firstCritical ? localTimestampMs(firstCritical.timestamp) : undefined;
  const lastCriticalMs = criticalEvents.length ? localTimestampMs(criticalEvents[criticalEvents.length - 1].timestamp) : undefined;
  const bands: IncidentBand[] = [];

  if (firstIssueMs > startMs) {
    bands.push({ label: "Normal", tone: "normal", startMs, endMs: firstIssueMs });
  }

  if (firstCriticalMs && firstIssueMs < firstCriticalMs) {
    bands.push({ label: "Warning cluster", tone: "warning", startMs: firstIssueMs, endMs: firstCriticalMs });
  } else if (!firstCriticalMs) {
    bands.push({ label: "Warning cluster", tone: "warning", startMs: firstIssueMs, endMs: Math.min(endMs, firstIssueMs + 2 * HOUR) });
  }

  if (firstCriticalMs) {
    const criticalEnd = Math.min(endMs, Math.max(lastCriticalMs ?? firstCriticalMs, firstCriticalMs + HOUR));
    bands.push({ label: "Critical", tone: "critical", startMs: firstCriticalMs, endMs: criticalEnd });
    if (criticalEnd < endMs) {
      bands.push({ label: "Recovery", tone: "recovery", startMs: criticalEnd, endMs: Math.min(endMs, criticalEnd + 2 * HOUR) });
    }
  }

  return bands.filter((band) => band.endMs > band.startMs);
}

function buildBookmarks(events: ParsedEvent[], startMs: number, endMs: number): Bookmark[] {
  const warning = events.find((event) => event.severity === "WARNING");
  const critical = events.find((event) => event.severity === "CRITICAL");
  const config = events.find((event) => event.category === "CONFIGURATION");
  const busiest = busiestWindow(events, startMs, endMs);
  const quiet = longestQuietGap(events);
  const recoveryStart = critical ? Math.min(endMs, localTimestampMs(critical.timestamp) + HOUR) : undefined;
  const bookmarks: Bookmark[] = [];

  if (warning) bookmarks.push({ id: "first-warning", label: "First warning", range: eventContextRange(warning, startMs, endMs), tone: "warning" });
  bookmarks.push({ id: "activity-spike", label: "Largest activity spike", range: { start: timestampFromMs(busiest.startMs), end: timestampFromMs(busiest.endMs), label: "Largest activity spike" } });
  if (config) bookmarks.push({ id: "config", label: "Config before trouble", range: eventContextRange(config, startMs, endMs) });
  if (critical) bookmarks.push({ id: "first-critical", label: "First critical issue", range: eventContextRange(critical, startMs, endMs), tone: "critical" });
  if (recoveryStart) bookmarks.push({ id: "recovery", label: "Recovery point", range: { start: timestampFromMs(recoveryStart), end: timestampFromMs(Math.min(endMs, recoveryStart + HOUR)), label: "Recovery point" } });
  if (quiet) bookmarks.push({ id: "quiet", label: "Longest quiet gap", range: { start: timestampFromMs(quiet.startMs), end: timestampFromMs(quiet.endMs), label: "Longest quiet gap" } });

  return bookmarks.slice(0, 7);
}

function buildCallouts(events: ParsedEvent[], bookmarks: Bookmark[], fallback: TimelineRange) {
  const critical = bookmarks.find((bookmark) => bookmark.id === "first-critical");
  const warning = bookmarks.find((bookmark) => bookmark.id === "first-warning");
  const config = bookmarks.find((bookmark) => bookmark.id === "config");
  const quiet = bookmarks.find((bookmark) => bookmark.id === "quiet");
  const fallbackCallout = { start: fallback.start, end: fallback.end, label: fallback.label };
  return [
    {
      detail: config ? "Configuration activity appears before the selected issue cluster." : "No configuration burst found before the selected issue cluster.",
      range: config?.range ?? fallbackCallout,
      title: "Config before trouble",
      tone: "config" as const,
    },
    {
      detail: critical ? "The first critical issue anchors the current investigation window." : "No critical issue found in this export.",
      range: critical?.range ?? fallbackCallout,
      title: "First critical issue",
      tone: "critical" as const,
    },
    {
      detail: warning ? "Warnings cluster before the highest-severity event." : "No warning cluster found in this export.",
      range: warning?.range ?? fallbackCallout,
      title: "Repeated warning cluster",
      tone: "warning" as const,
    },
    {
      detail: quiet ? "The largest quiet gap is useful as a baseline period." : `${events.length.toLocaleString()} events across this export.`,
      range: quiet?.range ?? fallbackCallout,
      title: "Longest quiet gap",
      tone: "info" as const,
    },
  ];
}

function busiestWindow(events: ParsedEvent[], startMs: number, endMs: number) {
  const windowMs = Math.max(HOUR, Math.min(3 * HOUR, endMs - startMs));
  const times = events.map((event) => localTimestampMs(event.timestamp)).sort((a, b) => a - b);
  let bestStart = startMs;
  let bestCount = 0;
  let right = 0;

  for (let left = 0; left < times.length; left += 1) {
    while (right < times.length && times[right] - times[left] <= windowMs) {
      right += 1;
    }
    const count = right - left;
    if (count > bestCount) {
      bestCount = count;
      bestStart = clamp(times[left], startMs, Math.max(startMs, endMs - windowMs));
    }
  }

  return { startMs: bestStart, endMs: Math.min(endMs, bestStart + windowMs) };
}

function longestQuietGap(events: ParsedEvent[]) {
  const sorted = sortOldest(events);
  let best: { startMs: number; endMs: number } | null = null;
  for (let index = 1; index < sorted.length; index += 1) {
    const previous = localTimestampMs(sorted[index - 1].timestamp);
    const current = localTimestampMs(sorted[index].timestamp);
    if (!best || current - previous > best.endMs - best.startMs) {
      best = { startMs: previous, endMs: current };
    }
  }
  return best;
}

function eventContextRange(event: ParsedEvent, startMs: number, endMs: number): TimelineRange {
  const anchorMs = localTimestampMs(event.timestamp);
  const rangeStart = Math.max(startMs, anchorMs - HOUR);
  const rangeEnd = Math.min(endMs, anchorMs + 2 * HOUR);
  return {
    start: timestampFromMs(rangeStart),
    end: timestampFromMs(rangeEnd),
    label: `${formatDateTime(timestampFromMs(rangeStart))} - ${formatTime(timestampFromMs(rangeEnd))}`,
  };
}

function inRange(event: ParsedEvent, range: ActiveRange) {
  const value = localTimestampMs(event.timestamp);
  return value >= range.startMs && value <= range.endMs;
}

function buildSequenceCards(events: ParsedEvent[], range: ActiveRange) {
  const definitions = [
    {
      detail: "Configuration changes appear before or during the selected operating sequence.",
      events: events.filter((event) => event.category === "CONFIGURATION"),
      title: "Configuration changes started the sequence",
    },
    {
      detail: "Watering activity forms the densest operational period inside this window.",
      events: events.filter((event) => event.category === "WATERING"),
      title: "Watering activity spiked",
    },
    {
      detail: "Warnings and critical events are grouped as the likely investigation sequence.",
      events: events.filter((event) => event.severity !== "INFO" || event.category === "TWO_WIRE"),
      title: "Issue signals appeared",
    },
  ];

  return definitions
    .filter((definition) => definition.events.length)
    .map((definition) => {
      const first = sortOldest(definition.events)[0];
      return {
        count: definition.events.length,
        detail: definition.detail,
        range: eventContextRange(first, range.startMs, range.endMs),
        time: formatTime(first.timestamp),
        title: definition.title,
      };
    });
}

function representative(events: ParsedEvent[]) {
  const sorted = sortOldest(events);
  const picked = new Map<string, ParsedEvent>();
  for (const event of sorted) {
    const key = event.severity !== "INFO" ? event.severity : event.category;
    if (!picked.has(key)) picked.set(key, event);
    if (picked.size >= 5) break;
  }
  return [...picked.values()].slice(0, 5);
}

function summarizeCompare(events: ParsedEvent[]) {
  if (!events.length) return "No controller events in this comparison window";
  const warnings = events.filter((event) => event.severity === "WARNING").length;
  const critical = events.filter((event) => event.severity === "CRITICAL").length;
  const watering = events.filter((event) => event.category === "WATERING").length;
  const config = events.filter((event) => event.category === "CONFIGURATION").length;
  const parts = [
    `${events.length.toLocaleString()} events`,
    critical ? `${critical} critical` : "",
    warnings ? `${warnings} warnings` : "",
    watering ? `${watering} watering` : "",
    config ? `${config} config changes` : "",
  ].filter(Boolean);
  return parts.join(", ");
}

function compareItems(events: ParsedEvent[]) {
  if (!events.length) return ["No nearby events found"];
  const categories = topCounts(events.map((event) => event.category.replace("_", " ")));
  const zones = new Set(events.map((event) => event.zoneNumber).filter(Boolean)).size;
  const programs = new Set(events.map((event) => event.programNumber).filter(Boolean)).size;
  return [
    categories.length ? `Most active: ${categories.join(", ")}` : "",
    zones ? `${zones} zones participated` : "No zone values found",
    programs ? `${programs} programs participated` : "No program values found",
  ].filter(Boolean);
}

function topCounts(values: string[]) {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([value, count]) => `${value.toLowerCase()} (${count})`);
}

function buildProximityItems(
  firstCritical: ParsedEvent | undefined,
  firstConfigBeforeIssue: ParsedEvent | undefined,
  selectedEvents: ParsedEvent[],
) {
  const watering = selectedEvents.filter((event) => event.category === "WATERING").length;
  const issue = selectedEvents.filter((event) => event.severity !== "INFO" || event.category === "TWO_WIRE").length;
  return [
    {
      detail:
        firstCritical && firstConfigBeforeIssue
          ? `Config changes ${Math.round((localTimestampMs(firstCritical.timestamp) - localTimestampMs(firstConfigBeforeIssue.timestamp)) / 60000)} minutes before the first critical event`
          : "No nearby configuration change found before the anchor issue",
      label: "Nearby setup",
    },
    {
      detail: watering ? `${watering.toLocaleString()} watering events overlap the selected window` : "No watering activity overlaps the selected window",
      label: "Nearby load",
    },
    {
      detail: issue ? `${issue.toLocaleString()} warning, critical, or two-wire events in the selected window` : "No issue signals in the selected window",
      label: "Nearby device signal",
    },
  ];
}

function buildStoryLead(warningCount: number, criticalCount: number) {
  if (criticalCount) return "The export contains a compact critical incident worth reviewing.";
  if (warningCount) return "The export contains warnings without a critical escalation.";
  return "The export reads as normal operating history.";
}

function buildStoryDetail(
  firstWarning: ParsedEvent | undefined,
  firstCritical: ParsedEvent | undefined,
  warningCount: number,
  criticalCount: number,
) {
  if (firstCritical) {
    return `Warnings begin around ${formatTime(firstWarning?.timestamp ?? firstCritical.timestamp)}, followed by ${criticalCount.toLocaleString()} critical event${criticalCount === 1 ? "" : "s"} and ${warningCount.toLocaleString()} warning${warningCount === 1 ? "" : "s"}.`;
  }
  if (firstWarning) {
    return `The first warning appears around ${formatTime(firstWarning.timestamp)}, with ${warningCount.toLocaleString()} warning${warningCount === 1 ? "" : "s"} across the file.`;
  }
  return "The timeline still shows watering, program, zone, and configuration rhythm for review.";
}

function formatDuration(value: number) {
  if (value <= 0) return "none";
  const minutes = Math.max(1, Math.round(value / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
}

function formatShortRangeEdge(timestamp: string) {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
    new Date(localTimestampMs(timestamp)),
  );
}
